import {
  Button,
  DateInput,
  MessageBox,
  NavigationButton,
  Select,
  TimeInput,
} from "@jutro/ui";
import dayjs from "dayjs";
import { useAtom } from "jotai";
import { Controller, SubmitHandler } from "react-hook-form";
import { ScheduleVisitTable } from "components/ScheduleVisitForm/ScheduleVisitTable";
import { scheduleVisitOrganizationIdAtom } from "components/ScheduleVisitForm/atoms";
import {
  ScheduleFormSchema,
  durationOptions,
} from "components/ScheduleVisitForm/form";
import { useScheduleVisitForm } from "components/ScheduleVisitForm/hooks/useScheduleVisitForm";
import { useScheduleVisitMutations } from "components/ScheduleVisitForm/hooks/useScheduleVisitMutations";
import { useTileModeConfig } from "components/ScheduleVisitForm/hooks/useTileModeConfig";
import { ScheduleVisitTileData } from "components/ScheduleVisitForm/types";
import { VisitCard } from "components/VisitCard";
import { Loader } from "components/new";
import { useDoctorGetVisitDataForEditFormQuery } from "lib/graphql/megaSchema";
import { useVojevodshipOptions } from "lib/hooks/useVojevodshipOptions";
import { useTileSystem } from "lib/tools/createWorkspace/useTileSystem";

type Props = {
  scheduleVisitTileData: ScheduleVisitTileData;
};

export const ScheduleVisitForm = ({ scheduleVisitTileData }: Props) => {
  const { id, mode } = scheduleVisitTileData;
  const [, setOrganizationId] = useAtom(scheduleVisitOrganizationIdAtom);

  const { data: visitData, loading: isVisitDataLoading } =
    useDoctorGetVisitDataForEditFormQuery({
      variables: {
        id,
      },
      skip: mode !== "edit" && mode !== "forceEdit",
    });

  const { loading, handleForceVisit } = useScheduleVisitMutations({
    scheduleVisitTileData,
    visitData,
  });

  const {
    methods,
    organizationsOptions,
    visitTypeOptions,
    officeOptions,
    doctorOptions,
    isViewForceable,
    isTelemedicalVisitType,
    showNoDeclarationMessageBox,
    shifts,
    isLoading,
    isPatientDataLoading,
  } = useScheduleVisitForm({ scheduleVisitTileData, visitData });

  const { vojevodshipsWithCities } = useVojevodshipOptions();

  const { watch, control, setValue, handleSubmit } = methods;

  const { visitType, organizationId } = watch();

  const { removeTile } = useTileSystem();

  const { title, toggleText, switchMode } = useTileModeConfig({
    tileId: id,
    mode,
  });

  const handleSwitchMode = () => {
    setValue("visitType", visitType);
    setValue("organizationId", organizationId);

    switchMode();
  };

  const onForceSubmit: SubmitHandler<ScheduleFormSchema> = async (data) => {
    await handleForceVisit(data);

    removeTile(id);
  };

  if (isVisitDataLoading || isPatientDataLoading) {
    return <Loader />;
  }

  return (
    <form
      className="flex h-full flex-col items-center gap-4 rounded-lg bg-white p-4"
      onSubmit={handleSubmit(onForceSubmit)}
    >
      <div className="flex w-full items-center justify-between">
        <h4 className="font-heading-4 text-jutro-new-warm-gray-800">{title}</h4>
        <div className="flex gap-2">
          {!isViewForceable && (
            <Button
              type="button"
              variant="secondary"
              size="condensed"
              onClick={handleSwitchMode}
              text={toggleText}
            />
          )}
          <NavigationButton
            onClick={() => {
              if (isViewForceable) {
                handleSwitchMode();
                return;
              }
              removeTile(id);
            }}
          />
        </div>
      </div>

      {(mode === "edit" || mode === "forceEdit") && visitData && (
        <VisitCard visit={visitData.doctorVisit} withActions={false} />
      )}

      <div className="flex w-full gap-4">
        <Controller
          name="visitType"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Select
              label="Typ wizyty*"
              placeholder="Wybierz typ wizyty"
              disabled={isLoading}
              value={visitTypeOptions.find((o) => o.value === value) ?? null}
              onChange={(o) =>
                setTimeout(() => {
                  onChange(o?.value);
                  setValue("staffId", null);
                  setValue("officeId", null);
                }, 100)
              }
              options={visitTypeOptions}
              error={error?.message}
            />
          )}
        />

        {isViewForceable && isTelemedicalVisitType ? (
          <Controller
            key="voivodeshipId"
            name="voivodeshipId"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Select
                noOptionsMessage="Brak dostępnych województw"
                label="Województwo*"
                placeholder="Wybierz województwo"
                isSearchable
                value={
                  vojevodshipsWithCities.find((o) => o.value === value) ?? null
                }
                onChange={(o) =>
                  setTimeout(() => {
                    onChange(o?.value);
                    setValue("staffId", null);
                    setValue("officeId", null);
                  }, 100)
                }
                options={vojevodshipsWithCities}
                error={error?.message}
              />
            )}
          />
        ) : (
          <Controller
            key="organizationId"
            name="organizationId"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Select
                noOptionsMessage="Brak dostępnych placówek"
                label="Placówka*"
                placeholder="Wybierz placówkę"
                isSearchable
                disabled={
                  (!isViewForceable && isTelemedicalVisitType) || isLoading
                }
                value={
                  isTelemedicalVisitType
                    ? null
                    : (organizationsOptions.find((o) => o.value === value) ??
                      null)
                }
                onChange={(o) =>
                  setTimeout(() => {
                    onChange(o?.value);
                    setValue("staffId", null);
                    setValue("officeId", null);

                    if (o?.value) {
                      setOrganizationId(o.value);
                    }
                  }, 100)
                }
                options={organizationsOptions.filter(
                  ({ value }) => value.length === 4,
                )}
                error={error?.message}
              />
            )}
          />
        )}
      </div>

      {isViewForceable && (
        <Controller
          name="officeId"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Select
              noOptionsMessage="Brak dostępnych gabinetów"
              label="Gabinet*"
              disabled={!organizationId || isTelemedicalVisitType}
              placeholder="Wybierz gabinet"
              value={officeOptions.find((o) => o.value === value) ?? null}
              onChange={(o) => onChange(o?.value)}
              options={officeOptions}
              error={
                !organizationId || isTelemedicalVisitType ? "" : error?.message
              }
            />
          )}
        />
      )}

      <Controller
        name="staffId"
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <Select
            noOptionsMessage="Brak dostępnych osób realizujących"
            label="Personel*"
            placeholder="Wybierz osobę realizującą"
            disabled={
              (!isViewForceable && doctorOptions.length < 2) || isLoading
            }
            isSearchable
            value={
              doctorOptions.find((o) =>
                value ? o.value === value : o.value === null,
              ) ?? null
            }
            onChange={(o) =>
              setTimeout(() => {
                onChange(o?.value ?? null);
              }, 100)
            }
            options={doctorOptions}
            error={error?.message}
          />
        )}
      />

      {showNoDeclarationMessageBox && (
        <MessageBox
          color="rose"
          text="Pacjent nie ma deklaracji, niektóre wizyty mogą być niedostępne"
        />
      )}

      {isViewForceable ? (
        <div className="flex w-full flex-col gap-8">
          <div className="flex w-full flex-wrap gap-2">
            <Controller
              name="plannedDate"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <div>
                  <DateInput
                    value={value ? dayjs(value).toDate() : null}
                    label="Data*"
                    onChange={(value) =>
                      onChange(
                        value
                          ? dayjs(value as Date).format("YYYY-MM-DD")
                          : dayjs().format("YYYY-MM-DD"),
                      )
                    }
                    minDate={dayjs().toDate()}
                    maxDate={dayjs().add(1, "year").toDate()}
                    error={error?.message}
                  />
                </div>
              )}
            />

            <Controller
              name="plannedTime"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TimeInput
                  value={value ?? ""}
                  label="Godzina*"
                  onChange={onChange}
                  error={error?.message}
                />
              )}
            />

            <Controller
              name="duration"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <div className="grow">
                  <Select
                    label="Długość wizyty*"
                    placeholder="Wybierz długość wizyty"
                    value={
                      durationOptions.find((o) => o.value === value) ?? null
                    }
                    onChange={(o) => onChange(o?.value)}
                    options={durationOptions}
                    error={error?.message}
                  />
                </div>
              )}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Button type="submit" text="Wymuś wizytę" loading={loading} />
            <Button
              type="button"
              text="Anuluj"
              variant="secondary"
              onClick={handleSwitchMode}
            />
          </div>
        </div>
      ) : (
        <ScheduleVisitTable
          shifts={shifts}
          visitType={visitType}
          isLoading={isLoading}
          scheduleVisitTileData={scheduleVisitTileData}
          visitData={visitData}
        />
      )}
    </form>
  );
};
